// ============================================
// TARJETAPRODUCTO.JS — Card mobile-first sin modal
// ============================================
// Click navega a /tienda/producto/:id_unico
// React.memo para evitar re-renders innecesarios al scrollear.
// Lazy loading + decoding async + URL optimizada (-m).

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock, ImageOff, Package, ShoppingCart, ClipboardList, Layers, Share2, CopyPlus, RefreshCw,
} from 'lucide-react';
import { tokens } from '../styles/tokens';
import {
  PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE,
} from './helpersClientes';
import { useMoneda } from './SelectorMoneda';
import { useIvaModo, conIvaSegun } from './SelectorIva';
import { useResponsive } from '../hooks/useResponsive';
import { optimizarImagen } from '../utils/imagenes';
import { hace, masViejoQue } from '../utils/tiempo';
import PreviewProductoTooltip from './PreviewProducto';
import PreviewProductoMobile from './PreviewProductoMobile';

const COLOR_BEEP = '#16a34a';
const COLOR_SYSCOM = COLOR_OSCURO;
const COLOR_PACTADO = tokens.warning;

function colorMargen(pct) {
  if (pct == null) return COLOR_GRIS_CLARO;
  if (pct >= 25) return tokens.success;
  if (pct >= 15) return tokens.warning;
  return tokens.danger;
}

// INVENTARIO = LA TIENDA (2026-10-19): con modoInventario la MISMA tarjeta ensena la cara del almacen (existencia,
// disponible, apartado) en vez del precio/margen; los filtros, la foto y el detalle son los mismos.
function TarjetaProducto({ producto, onAgregarCarrito, onAgregarLista, onAgregarColeccion, onCompartir, onVarianteChange, onDuplicarMisProductos, onRefrescarSyscom, modoCotizacion = false, modoInventario = false, onHojaCompras }) {
  const navigate = useNavigate();
  const { formatear } = useMoneda();
  // Switch compartido Con IVA / Sin IVA (solo afecta el precio MOSTRADO al cliente).
  const [ivaModo] = useIvaModo();
  const { isMobile, isMobileXs, isTablet } = useResponsive();
  // Alto tactil comodo: la tablet TAMBIEN se toca con el dedo, no solo el movil.
  // (Se detecto en tablet 768: el boton quedaba en 30px, por debajo del minimo de 44.)
  const esTactil = isMobile || isTablet;
  const [hover, setHover] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewMobileAbierto, setPreviewMobileAbierto] = useState(false);
  // REFRESCO-SYSCOM: spinner mientras se consulta al proveedor por ESTE producto.
  const [refrescando, setRefrescando] = useState(false);
  const tarjetaRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const montadoRef = useRef(true);
  useEffect(() => () => { montadoRef.current = false; }, []);

  const esBeep = producto.fuente === 'beep';
  const stock = producto.stock;
  const sinStock = stock != null && stock <= 0;
  // STOCK-MIS-PRODUCTOS: en Mis Productos el stock es el PROPIO y solo AVISA;
  // el bloqueo de "agregar" aplica unicamente a productos Syscom sin existencia.
  const bloqueaAgregar = sinStock && !esBeep;

  const precioEfectivo = Number(producto.precio_efectivo_mxn || 0);

  // Variantes embebidas (beep_producto o beep_cable): selector de presentacion/nivel.
  const variantesProd = (producto.tiene_variantes === true && Array.isArray(producto.variantes))
    ? producto.variantes : [];
  const tieneVariantesSel = variantesProd.length > 0;
  // Arranca en la variante GUARDADA (variante_seleccionada_id) si existe en la lista; si no,
  // en la primera (orden 0 / Basica). Asi al volver a la lista NO se resetea a Basica.
  const varInicial = tieneVariantesSel
    ? ((producto.variante_seleccionada_id != null
        && variantesProd.some(v => String(v.id) === String(producto.variante_seleccionada_id)))
        ? producto.variante_seleccionada_id
        : variantesProd[0].id)
    : null;
  const [varSelId, setVarSelId] = useState(varInicial);
  const vSel = tieneVariantesSel
    ? (variantesProd.find(v => String(v.id) === String(varSelId)) || variantesProd[0])
    : null;
  // Precio grande de la tarjeta: el de la variante elegida si es variable; si no, el normal.
  // (precioEfectivo respeta precios pactados por cliente; NO se rompe esa logica).
  const precioCard = tieneVariantesSel ? (Number(vSel.precio_venta_mxn) || 0) : precioEfectivo;

  // TIENDA-TARJETAS: lista (publico) tachada, descuento y linea interna (costo+margen).
  // Para Beep estos campos llegan null/0 y los elementos simplemente no se muestran.
  const precioListaMxn = Number(producto.precio_lista_mxn || 0);
  const descuentoLista = Number(producto.descuento_vs_lista_pct || 0);
  const costoCard = tieneVariantesSel
    ? (Number(vSel.costo_interno_mxn) || 0)
    : Number(producto.costo_mxn || 0);
  const margenCard = producto.margen_pct != null
    ? Number(producto.margen_pct)
    : (precioCard > 0 && costoCard > 0 ? ((precioCard - costoCard) / precioCard * 100) : null);
  // FIX-MARGEN-SYSCOM: redondear UNA vez y usar ESE entero tanto para el texto como para
  // el color, asi "25%" siempre sale verde (nunca "dice 25% pero esta en naranja").
  const margenInt = margenCard != null ? Math.round(margenCard) : null;
  const muestraLista = precioListaMxn > precioCard; // tachado solo si la lista supera el precio mostrado

  // Cantidad a agregar (stepper en la tarjeta). Minimo 1, sin tope (lo maneja el carrito).
  const [cantidad, setCantidad] = useState(1);
  // Cara de inventario: cantidades vienen como texto (NUMERIC sin redondear); aqui solo se pintan.
  const invExistencia = Number(producto.inv_existencia || 0), invDisponible = Number(producto.inv_disponible || 0), invApartado = Number(producto.inv_apartado || 0);
  const fmtCant = (n) => Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 4 });
  const fmtDia = (v) => { if (!v) return ''; try { return new Date(String(v).slice(0, 10) + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return ''; } };

  // En mobile: hover desactivado para evitar repaints en scroll
  const aplicarHover = !isMobile && hover;

  const irADetalle = useCallback(() => {
    navigate(`/tienda/producto/${encodeURIComponent(producto.id_unico)}`);
  }, [navigate, producto.id_unico]);

  const stop = (e, fn) => {
    e.stopPropagation();
    fn && fn();
  };

  // REFRESCO-SYSCOM: solo aplica a productos del proveedor (los propios de Mis
  // Productos se editan a mano) y solo si el padre proporciono el handler.
  const puedeRefrescar = !esBeep && typeof onRefrescarSyscom === 'function';
  const textoActualizado = puedeRefrescar ? hace(producto.actualizado_at) : null;
  const datoViejo = puedeRefrescar && masViejoQue(producto.actualizado_at, 7);

  const handleRefrescar = useCallback(async () => {
    if (!onRefrescarSyscom || refrescando) return;
    setRefrescando(true);
    try {
      await onRefrescarSyscom(producto);
    } finally {
      // La tarjeta puede desmontarse si la lista se recarga: no tocar estado ahi.
      if (montadoRef.current) setRefrescando(false);
    }
  }, [onRefrescarSyscom, producto, refrescando]);

  const handleMouseEnter = () => {
    if (isMobile) return;
    setHover(true);
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setPreviewVisible(true), 500);
  };
  const handleMouseLeave = () => {
    if (isMobile) return;
    setHover(false);
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setPreviewVisible(false);
  };

  // Boton del stepper de cantidad (- / +). Mismo lenguaje visual que el select de variantes.
  const estiloStep = {
    width: 40, minHeight: 40, fontSize: 18, fontWeight: 700, fontFamily: PP,
    color: tokens.text, background: '#fff', border: `1px solid ${COLOR_BORDE}`,
    borderRadius: 8, cursor: 'pointer', touchAction: 'manipulation', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  return (
    <>
    <div
      ref={tarjetaRef}
      onClick={irADetalle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        background: '#fff',
        border: `1px solid ${aplicarHover ? COLOR_SYSCOM : COLOR_BORDE}`,
        borderRadius: 12,
        overflow: 'hidden',
        fontFamily: PP, color: COLOR_GRIS,
        cursor: 'pointer',
        transition: isMobile ? 'none' : 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        boxShadow: aplicarHover ? '0 8px 24px rgba(24,95,165,0.15)' : '0 1px 2px rgba(0,0,0,0.04)',
        transform: aplicarHover ? 'translateY(-2px)' : 'translateY(0)',
        WebkitTapHighlightColor: 'transparent',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Foto (mas compacta 4:3) con badges */}
      <div style={{
        position: 'relative',
        width: '100%', aspectRatio: '4 / 3',
        background: tokens.surfaceSubtle,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {isMobile && (
          <button
            onClick={(e) => { e.stopPropagation(); setPreviewMobileAbierto(true); }}
            aria-label="Ver caracteristicas"
            style={{
              // Area tactil 44x44 (UI-1.D); el circulo visible interior sigue siendo 28px.
              // Anclado en la esquina (top/right 0): el circulo de 28 centrado queda a 8px del borde, igual que antes.
              position: 'absolute', top: 0, right: 0, zIndex: 5,
              width: 44, height: 44, minWidth: 44, minHeight: 44,
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
            }}
          >
            <span style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)',
              border: `1px solid ${COLOR_BORDE}`,
              color: COLOR_OSCURO,
              fontSize: 14, fontWeight: 700, fontFamily: PP,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontStyle: 'italic',
            }}>i</span>
          </button>
        )}
        {(producto.es_pactado || descuentoLista > 0) && (
          <div style={{
            position: 'absolute', top: 8, left: 8, zIndex: 2,
            display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start',
          }}>
            {producto.es_pactado && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                background: COLOR_PACTADO, color: '#fff', letterSpacing: 0.5,
              }}><Lock size={11} strokeWidth={2} /> PACTADO</span>
            )}
            {descuentoLista > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 800,
                background: tokens.danger, color: '#fff', letterSpacing: 0.3,
              }}>-{Number(descuentoLista).toFixed(0)}%</span>
            )}
          </div>
        )}

        {producto.imagen_url ? (
          <img
            src={optimizarImagen(producto.imagen_url, 'm')}
            alt={producto.nombre}
            loading="lazy"
            decoding="async"
            style={{
              width: '100%', height: '100%', objectFit: 'contain',
              padding: isMobileXs ? 12 : 16,
              display: 'block',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <ImageOff size={44} strokeWidth={1.5} color="#cbd5e1" aria-label="Sin imagen" />
        )}
      </div>

      <div style={{
        padding: isMobile ? 10 : 12,
        display: 'flex', flexDirection: 'column', gap: 4, flex: 1,
      }}>
        {producto.marca && (
          <div style={{
            fontSize: 11, fontWeight: 700, color: COLOR_OSCURO,
            textTransform: 'uppercase', letterSpacing: 0.5,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{producto.marca}</div>
        )}

        <div style={{
          fontSize: isMobile ? 13 : 14, fontWeight: 600, color: tokens.text, lineHeight: 1.3,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', minHeight: isMobile ? 34 : 36,
        }}>{producto.nombre}</div>

        {producto.modelo && (
          <div style={{
            fontSize: isMobile ? 12 : 13, color: '#6d28d9', fontWeight: 800, fontFamily: 'monospace',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{producto.modelo}</div>
        )}

        {modoInventario && (
          <div style={{ marginTop: 2 }}>
            {invExistencia > 0 ? (
              <>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: invDisponible > 0 ? tokens.text : tokens.warning, lineHeight: 1.1 }}>
                  {fmtCant(invDisponible)} <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: COLOR_GRIS_CLARO }}>{producto.unidad && producto.unidad !== 'pieza' ? producto.unidad : 'pza'} disponibles</span>
                </div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginTop: 2 }}>
                  Existencia <strong style={{ color: tokens.text }}>{fmtCant(invExistencia)}</strong> · apartado <strong style={{ color: invApartado > 0 ? tokens.warning : tokens.text }}>{fmtCant(invApartado)}</strong>
                </div>
                {producto.costo_promedio_almacen != null && (
                  <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600 }}>Costo prom. almacén {formatear(Number(producto.costo_promedio_almacen), 'MXN')}</div>
                )}
                <div style={{ fontSize: 10.5, color: producto.inv_garantia_proveedor_proxima ? tokens.success : COLOR_GRIS_CLARO, fontWeight: 600 }}>
                  {producto.inv_garantia_proveedor_proxima ? `🛡️ Garantía proveedor hasta ${fmtDia(producto.inv_garantia_proveedor_proxima)}` : '🛡️ Garantía proveedor por confirmar'}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 800, color: tokens.danger, lineHeight: 1.1 }}>Sin inventario</div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginTop: 2 }}>
                  {esBeep ? 'Mis Productos' : (stock != null ? `Syscom tiene ${fmtCant(stock)}` : 'Syscom: sin dato')}
                  {invApartado > 0 ? ` · apartado ${fmtCant(invApartado)}` : ''}
                </div>
                {onHojaCompras && (
                  <button type="button" onClick={(e) => stop(e, () => onHojaCompras(producto))} style={{ marginTop: 4, background: 'transparent', border: 'none', padding: '4px 0', minHeight: 28, color: COLOR_OSCURO, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: PP, textDecoration: 'underline' }}>🛒 Ver en Compras</button>
                )}
              </>
            )}
          </div>
        )}
        {!modoInventario && <div style={{ marginTop: 2 }}>
          {muestraLista && (
            <div style={{
              fontSize: 11, color: tokens.textMuted, textDecoration: 'line-through',
              fontWeight: 500,
            }}>
              Lista {formatear(conIvaSegun(ivaModo, precioListaMxn), 'MXN')}
            </div>
          )}
          <div style={{
            fontSize: isMobile ? 18 : 22, fontWeight: 800, color: tokens.text, lineHeight: 1.1,
          }}>
            {formatear(conIvaSegun(ivaModo, precioCard), 'MXN')}
            {producto.unidad && producto.unidad !== 'pieza' && (
              <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: tokens.textMuted }}> / {producto.unidad}</span>
            )}
          </div>
        </div>}

        {/* Linea de costo/margen (info interna). Margen + utilidad en color por umbral
            (verde sano / ambar medio / rojo bajo). */}
        {!modoInventario && (costoCard > 0 || margenCard != null) && (
          <div style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4,
            fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginTop: 2,
          }}>
            {costoCard > 0 && <span>Costo {formatear(conIvaSegun(ivaModo, costoCard), 'MXN')}</span>}
            {margenInt != null && (
              <span style={{ color: colorMargen(margenInt), fontWeight: 700 }}>
                {costoCard > 0 ? '· ' : ''}margen {margenInt}%
                {costoCard > 0 && ` · gana ${formatear(conIvaSegun(ivaModo, precioCard - costoCard), 'MXN')}`}
              </span>
            )}
          </div>
        )}

        {!modoInventario && (stock != null || puedeRefrescar) && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8, flexWrap: 'wrap', marginTop: 2,
          }}>
            {stock != null ? (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 12, fontWeight: 600,
                color: sinStock ? tokens.danger : (stock > 10 ? tokens.success : tokens.warning),
              }}>
                <Package size={12} strokeWidth={2} /> {esBeep
                  ? (sinStock ? 'Sin inventario' : `Inventario: ${stock}`)
                  : (sinStock ? 'Sin stock' : `Stock: ${stock}`)}
                {/* DESCONTINUADOS-SYSCOM: etiqueta discreta para que el vendedor entienda
                    por que esta en cero (Syscom dejo de listarlo). Sigue visible. */}
                {!esBeep && producto.descontinuado && (
                  <span style={{ fontSize: 11, fontWeight: 500, color: COLOR_GRIS_CLARO, marginLeft: 2 }}>
                    · Descontinuado por proveedor
                  </span>
                )}
              </span>
            ) : <span />}

            {/* REFRESCO-SYSCOM: consulta a Syscom SOLO este producto y actualiza
                existencias/precios al instante. Va pegado a la linea de stock
                porque es justo ahi donde nace la duda ("dice sin stock, ¿seguro?"). */}
            {puedeRefrescar && (
              <button
                type="button"
                onClick={(e) => stop(e, handleRefrescar)}
                disabled={refrescando}
                title={refrescando ? 'Consultando a Syscom...' : 'Consultar existencias y precio actuales en Syscom'}
                aria-label="Actualizar desde Syscom"
                aria-busy={refrescando}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  // Area tactil comoda en movil Y tablet sin engordar la tarjeta.
                  minHeight: esTactil ? 44 : 28, padding: esTactil ? '0 8px' : '2px 6px',
                  background: 'transparent', border: 'none', borderRadius: 6,
                  color: refrescando ? COLOR_GRIS_CLARO : COLOR_OSCURO,
                  fontSize: 11, fontWeight: 700, fontFamily: PP,
                  cursor: refrescando ? 'wait' : 'pointer',
                  touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >
                <RefreshCw
                  size={12}
                  strokeWidth={2.5}
                  style={refrescando ? { animation: 'spin 0.9s linear infinite' } : undefined}
                />
                {refrescando ? 'Consultando…' : 'Actualizar'}
              </button>
            )}
          </div>
        )}

        {/* Antiguedad del dato. Se marca en ambar cuando pasa de una semana:
            es la senal de "ojo, esto puede estar desfasado". */}
        {!modoInventario && puedeRefrescar && textoActualizado && (
          <div style={{
            fontSize: 10, fontWeight: 600,
            color: datoViejo ? tokens.warning : COLOR_GRIS_CLARO,
          }}>
            Actualizado {textoActualizado}
          </div>
        )}

        {tieneVariantesSel && (
          <select
            value={String(varSelId)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              const nueva = e.target.value;
              setVarSelId(nueva);
              // En contexto de lista/coleccion (onVarianteChange presente) -> PERSISTIR.
              // En La Tienda (sin onVarianteChange) -> solo eleccion del momento.
              if (onVarianteChange) onVarianteChange(nueva);
            }}
            aria-label="Elegir presentacion"
            style={{
              marginTop: 8, width: '100%', minHeight: 40,
              padding: '8px 10px', fontSize: 13, fontFamily: PP,
              color: tokens.text, background: '#fff',
              border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
              cursor: 'pointer', touchAction: 'manipulation',
            }}
          >
            {variantesProd.map(v => (
              <option key={v.id} value={String(v.id)}>{v.etiqueta}</option>
            ))}
          </select>
        )}

        {modoInventario && (
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 8 }}>
            <button
              onClick={(e) => stop(e, irADetalle)}
              style={{ background: COLOR_OSCURO, color: '#fff', border: 'none', borderRadius: 8, padding: isMobile ? '11px 8px' : '12px 14px', fontSize: isMobile ? 13 : 14, fontWeight: 700, cursor: 'pointer', fontFamily: PP, minHeight: isMobile ? 44 : 40, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap', overflow: 'hidden' }}
            ><Package size={14} strokeWidth={2} style={{ flexShrink: 0 }} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{invExistencia > 0 ? 'Lotes, apartar y surtir' : 'Ver detalle'}</span></button>
          </div>
        )}
        {!modoInventario && <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 8 }}>
          {/* Stepper de cantidad (todas las tarjetas). Minimo 1, sin tope. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              aria-label="Disminuir cantidad"
              onClick={(e) => { e.stopPropagation(); setCantidad(c => Math.max(1, c - 1)); }}
              style={estiloStep}
            >-</button>
            <InputCantidadTarjeta valor={cantidad} onFijar={setCantidad} />
            <button
              type="button"
              aria-label="Aumentar cantidad"
              onClick={(e) => { e.stopPropagation(); setCantidad(c => c + 1); }}
              style={estiloStep}
            >+</button>
          </div>
          <button
            onClick={(e) => stop(e, () => {
              if (!onAgregarCarrito) return;
              const prodAEnviar = tieneVariantesSel
                ? { ...producto, referencia_id: `${producto.referencia_id}:${vSel.id}` }
                : producto;
              onAgregarCarrito(prodAEnviar, cantidad);
              setCantidad(1);
            })}
            disabled={bloqueaAgregar}
            style={{
              background: bloqueaAgregar ? COLOR_GRIS_CLARO : COLOR_OSCURO,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: isMobile ? '11px 8px' : '12px 14px',
              fontSize: isMobile ? 13 : 14,
              fontWeight: 700,
              cursor: bloqueaAgregar ? 'not-allowed' : 'pointer',
              fontFamily: PP,
              opacity: bloqueaAgregar ? 0.6 : 1,
              minHeight: isMobile ? 44 : 40,
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              whiteSpace: 'nowrap', overflow: 'hidden',
            }}
          >
            <ShoppingCart size={14} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {modoCotizacion
                ? (isMobile ? 'A la cotización' : 'Agregar a la cotización')
                : (isMobile ? 'Agregar' : 'Agregar al carrito')}
            </span>
          </button>
          <div style={{ display: 'flex', gap: 6 }}>
            {onAgregarLista && (
              <button
                onClick={(e) => stop(e, () => onAgregarLista(producto))}
                style={btnSecundario(isMobile)}
              ><ClipboardList size={12} strokeWidth={2} /> Lista</button>
            )}
            {onAgregarColeccion && (
              <button
                onClick={(e) => stop(e, () => onAgregarColeccion(producto))}
                style={btnSecundario(isMobile)}
              ><Layers size={12} strokeWidth={2} /> Colección</button>
            )}
            {onCompartir && (
              <button
                onClick={(e) => stop(e, () => onCompartir(producto))}
                style={btnSecundario(isMobile)}
              ><Share2 size={12} strokeWidth={2} /> Compartir</button>
            )}
          </div>
          {/* DUPLICAR-MIS-PRODUCTOS: solo en tarjetas Syscom. Copia los datos a un
              alta pre-llenada de Mis Productos donde manda el inventario PROPIO
              (el caso tipico: Syscom sin stock pero Ricardo SI lo tiene). */}
          {onDuplicarMisProductos && !esBeep && (
            <button
              onClick={(e) => stop(e, () => onDuplicarMisProductos(producto))}
              style={{ ...btnSecundario(isMobile), flex: 'none', width: '100%', minHeight: isMobile ? 44 : 36 }}
            ><CopyPlus size={12} strokeWidth={2} /> Duplicar a Mis Productos</button>
          )}
        </div>}
      </div>
    </div>

    {!isMobile && (
      <PreviewProductoTooltip
        producto={producto}
        anchorRef={tarjetaRef}
        visible={previewVisible}
      />
    )}
    {isMobile && (
      <PreviewProductoMobile
        producto={producto}
        abierto={previewMobileAbierto}
        onCerrar={() => setPreviewMobileAbierto(false)}
        onVerCompleto={() => { setPreviewMobileAbierto(false); irADetalle(); }}
      />
    )}
    </>
  );
}

function btnSecundario(isMobile) {
  return {
    flex: 1,
    background: '#fff',
    color: COLOR_GRIS,
    border: `1px solid ${COLOR_BORDE}`,
    borderRadius: 8,
    padding: isMobile ? '8px 6px' : '8px 6px',
    fontSize: isMobile ? 11 : 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: PP,
    minHeight: isMobile ? 36 : 36,
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };
}

// Input editable de cantidad a agregar (entre los botones - y +). Solo enteros,
// minimo 1; permite borrar y teclear varios digitos. Cantidad local (no PATCH).
// stopPropagation en todos los handlers para no disparar la navegacion de la tarjeta.
function InputCantidadTarjeta({ valor, onFijar }) {
  const [texto, setTexto] = useState(String(valor));
  const enfocadoRef = useRef(false);

  useEffect(() => {
    if (!enfocadoRef.current) setTexto(String(valor));
  }, [valor]);

  const onChange = (e) => {
    e.stopPropagation();
    const limpio = (e.target.value || '').replace(/[^0-9]/g, '');
    setTexto(limpio);
    const n = parseInt(limpio, 10);
    if (Number.isFinite(n) && n >= 1) onFijar(n);
  };

  const normalizar = () => {
    const n = parseInt(texto, 10);
    const final = Number.isFinite(n) && n >= 1 ? n : 1;
    setTexto(String(final));
    onFijar(final);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      aria-label="Cantidad a agregar"
      value={texto}
      onClick={(e) => e.stopPropagation()}
      onChange={onChange}
      onFocus={(e) => { e.stopPropagation(); enfocadoRef.current = true; e.target.select(); }}
      onBlur={() => { enfocadoRef.current = false; normalizar(); }}
      onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') e.target.blur(); }}
      style={{
        flex: 1, minWidth: 0, textAlign: 'center', minHeight: 40,
        fontSize: 14, fontWeight: 700, fontFamily: PP, color: tokens.text,
        border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
        background: '#fff', outline: 'none', padding: '0 4px',
        MozAppearance: 'textfield',
      }}
    />
  );
}

// React.memo: solo re-renderiza si cambia id_unico, precio efectivo,
// es_pactado o stock — los handlers vienen memoizados desde el padre.
function eqProducto(prev, next) {
  const a = prev.producto, b = next.producto;
  return a.id_unico === b.id_unico
    && a.precio_efectivo_mxn === b.precio_efectivo_mxn
    && a.precio_lista_mxn === b.precio_lista_mxn
    && a.descuento_vs_lista_pct === b.descuento_vs_lista_pct
    && a.costo_mxn === b.costo_mxn
    && a.margen_pct === b.margen_pct
    && a.es_pactado === b.es_pactado
    && a.stock === b.stock
    // REFRESCO-SYSCOM: tras actualizar cambian nombre/imagen y la marca de tiempo.
    // Sin estos, la tarjeta se quedaria pintada con el dato viejo.
    && a.nombre === b.nombre
    && a.imagen_url === b.imagen_url
    && a.actualizado_at === b.actualizado_at
    // INVENTARIO = LA TIENDA: la cara del almacen tambien cuenta para re-pintar.
    && a.inv_existencia === b.inv_existencia && a.inv_apartado === b.inv_apartado && a.inv_disponible === b.inv_disponible
    && a.costo_promedio_almacen === b.costo_promedio_almacen && a.inv_garantia_proveedor_proxima === b.inv_garantia_proveedor_proxima
    && prev.modoInventario === next.modoInventario && prev.onHojaCompras === next.onHojaCompras
    && prev.onRefrescarSyscom === next.onRefrescarSyscom
    && prev.modoCotizacion === next.modoCotizacion
    && prev.onAgregarCarrito === next.onAgregarCarrito
    && prev.onAgregarLista === next.onAgregarLista
    && prev.onAgregarColeccion === next.onAgregarColeccion
    && prev.onCompartir === next.onCompartir
    && prev.onDuplicarMisProductos === next.onDuplicarMisProductos;
}

export default React.memo(TarjetaProducto, eqProducto);
